/**
 * Safety API Service
 * Handles blocking, reporting, and unmatching functionality.
 * Centralizes safety-related API calls for use across the app.
 */

import { apiService } from './apiService';

// ==========================================
// TYPES
// ==========================================

export type ReportReason =
  | 'inappropriate_content'
  | 'harassment'
  | 'spam'
  | 'fake_profile'
  | 'underage'
  | 'other';

export interface ReportRequest {
  reportedProfileId: string;
  reason: ReportReason;
  details?: string;
}

export interface BlockRequest {
  blockedProfileId: string;
}

export interface UnmatchRequest {
  matchId: string;
}

export interface SafetyActionResult {
  success: boolean;
  error?: string;
}

export interface BlockedUser {
  profileId: string;
  displayName: string;
  blockedAt: string;
}

export interface Report {
  id: string;
  reportedProfileId: string;
  reason: ReportReason;
  details?: string;
  status: 'pending' | 'reviewed' | 'resolved';
  createdAt: string;
}

// ==========================================
// REPORT REASONS
// ==========================================

export const REPORT_REASONS: { value: ReportReason; label: string; description: string }[] = [
  {
    value: 'inappropriate_content',
    label: 'Inappropriate Content',
    description: 'Photos or messages that are offensive or violate community guidelines',
  },
  {
    value: 'harassment',
    label: 'Harassment or Bullying',
    description: 'Unwanted, aggressive, or threatening behavior',
  },
  {
    value: 'spam',
    label: 'Spam or Scam',
    description: 'Promotional content, scam attempts, or suspicious activity',
  },
  {
    value: 'fake_profile',
    label: 'Fake Profile',
    description: 'Impersonation, catfishing, or using someone else\'s photos',
  },
  {
    value: 'underage',
    label: 'Underage User',
    description: 'Person appears to be under 18 years old',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Another reason not listed above',
  },
];

// ==========================================
// BLOCK FUNCTIONS
// ==========================================

/**
 * Block a user by their profile ID.
 * This will prevent them from appearing in discovery and messaging.
 *
 * Social graph note (social.graph): once the backend graph lands, `block`
 * ALSO severs the connection graph server-side — inside the same
 * SafetyService.BlockUserAsync transaction it deletes the connections edge,
 * cancels any pending connection requests both directions, and writes a
 * `connection_audit('block')` row. Block stays authoritative here on
 * `/api/safety/*`; no `social.*` flag can weaken it. See
 * features/profiles-and-social-connections/migrations/backend-handover.md §2.
 */
export async function blockUser(profileId: string): Promise<SafetyActionResult> {
  const response = await apiService.post<{ success: boolean }>('/api/safety/block', {
    blockedProfileId: profileId,
  });

  if (response.error) {
    console.error('Failed to block user:', response.error);
    return { success: false, error: response.error };
  }

  return { success: response.data?.success ?? false };
}

/**
 * Unblock a user by their profile ID
 */
export async function unblockUser(profileId: string): Promise<SafetyActionResult> {
  const response = await apiService.post<{ success: boolean }>(`/api/safety/unblock`, {
    profileId,
  });

  if (response.error) {
    console.error('Failed to unblock user:', response.error);
    return { success: false, error: response.error };
  }

  return { success: response.data?.success ?? false };
}

/**
 * Get list of blocked users
 */
export async function getBlockedUsers(): Promise<BlockedUser[]> {
  const response = await apiService.get<BlockedUser[]>('/api/safety/blocked');

  if (response.error) {
    console.error('Failed to get blocked users:', response.error);
    return [];
  }

  return response.data || [];
}

// ==========================================
// REPORT FUNCTIONS
// ==========================================

/**
 * Report a user
 */
export async function reportUser(
  profileId: string,
  reason: ReportReason,
  details?: string
): Promise<SafetyActionResult> {
  const response = await apiService.post<{ success: boolean; reportId?: string }>('/api/safety/report', {
    reportedProfileId: profileId,
    reason,
    details,
  });

  if (response.error) {
    console.error('Failed to report user:', response.error);
    return { success: false, error: response.error };
  }

  return { success: response.data?.success ?? false };
}

/**
 * Get user's submitted reports
 */
export async function getMyReports(): Promise<Report[]> {
  const response = await apiService.get<Report[]>('/api/safety/reports');

  if (response.error) {
    console.error('Failed to get reports:', response.error);
    return [];
  }

  return response.data || [];
}

// ==========================================
// UNMATCH FUNCTIONS
// ==========================================

/**
 * Unmatch with a user by match ID
 * This removes the match connection
 */
export async function unmatch(matchId: string): Promise<SafetyActionResult> {
  const response = await apiService.post<{ success: boolean }>(`/api/discovery/matches/${matchId}/unmatch`, {});

  if (response.error) {
    console.error('Failed to unmatch:', response.error);
    return { success: false, error: response.error };
  }

  return { success: response.data?.success ?? false };
}

// ==========================================
// COMBINED ACTIONS
// ==========================================

/**
 * Block and report a user in a single action
 * Useful for severe violations
 */
export async function blockAndReport(
  profileId: string,
  reason: ReportReason,
  details?: string
): Promise<SafetyActionResult> {
  // First report
  const reportResult = await reportUser(profileId, reason, details);
  if (!reportResult.success) {
    return reportResult;
  }

  // Then block
  const blockResult = await blockUser(profileId);
  if (!blockResult.success) {
    // Report was successful but block failed
    console.warn('Report succeeded but block failed');
    return { success: true, error: 'User reported but blocking failed' };
  }

  return { success: true };
}

/**
 * Unmatch and optionally block a user
 */
export async function unmatchAndBlock(
  matchId: string,
  profileId: string
): Promise<SafetyActionResult> {
  // First unmatch
  const unmatchResult = await unmatch(matchId);
  if (!unmatchResult.success) {
    return unmatchResult;
  }

  // Then block
  const blockResult = await blockUser(profileId);
  if (!blockResult.success) {
    console.warn('Unmatch succeeded but block failed');
    return { success: true, error: 'Unmatched but blocking failed' };
  }

  return { success: true };
}

// ==========================================
// EXPORT ALL
// ==========================================

export const safetyService = {
  // Block
  blockUser,
  unblockUser,
  getBlockedUsers,

  // Report
  reportUser,
  getMyReports,

  // Unmatch
  unmatch,

  // Combined
  blockAndReport,
  unmatchAndBlock,

  // Constants
  REPORT_REASONS,
};
